import React, { useContext, useState } from 'react';
import { CheckCircleOutlined, EditOutlined } from '@ant-design/icons';
import { Button, Tooltip } from 'antd';
import { TablePaginationConfig } from 'antd/es/table';
import { ColumnProps } from 'antd/lib/table';
import { SorterResult } from 'antd/lib/table/interface';

import ReportContext from '@totem/components/billingRates/BillingRateReportContext';
import { BillingRate } from '@totem/components/billingRates/dataTypes';
import TableTitle from '@totem/components/billingRates/TableTitle';
import IBTable from '@totem/components/ui_components/antDesignOverrides/IBTable';
import Currency from '@totem/components/ui_components/widgets/currency/Currency';
import Hours from '@totem/components/ui_components/widgets/hours/hours';
import colors from '@totem/styles/colors';
import IBDIcon from '@totem/styles/Icon';
import { isNotNull } from '@totem/utilities/common';

import AddEditDialog from './AddEditDialog';

import './billingRateReport.css';

const styles = {
  button: {
    marginRight: '4px',
  },
};

const ReportTable = () => {
  const [action, setAction] = useState<string>('');
  const [selectedForEdit, setSelectedForEdit] = useState<BillingRate>(null);
  const [showEditDialog, setShowEditDialog] = useState<boolean>(false);
  const { reportData, loading, input, setInput, totalRecords, onAction } =
    useContext(ReportContext);

  const handleEditClicked = (record: BillingRate) => {
    setShowEditDialog(true);

    setSelectedForEdit(record);

    setAction('edit');
  };

  const columns: ColumnProps<BillingRate>[] = [
    {
      title: <TableTitle>Name</TableTitle>,
      dataIndex: 'name',
      key: 'name',
      render: (_, record: BillingRate) => <div>{record.name}</div>,
      //className: 'tableAlignTop',
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.name === compB.name ? 0 : compA.name > compB.name ? 1 : -1,
    },
    {
      title: <TableTitle>Type</TableTitle>,
      dataIndex: 'type',
      key: 'type',
      render: (_, record: BillingRate) => <div>{record.type}</div>,
      //className: 'tableAlignTop',
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.type === compB.type ? 0 : compA.type > compB.type ? 1 : -1,
    },
    {
      title: <TableTitle>Email</TableTitle>,
      dataIndex: 'email',
      key: 'email',
      render: (_, record: BillingRate) => <div>{record.email}</div>,
      //className: 'tableAlignTop',
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.email === compB.email ? 0 : compA.email > compB.email ? 1 : -1,
    },
    {
      title: <TableTitle>Department</TableTitle>,
      dataIndex: 'department',
      key: 'department',
      render: (_, record: BillingRate) => <div>{record.department}</div>,
      //className: 'tableAlignTop',
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.department === compB.department
          ? 0
          : compA.department > compB.department
            ? 1
            : -1,
    },
    {
      title: <TableTitle>Dept. Lead</TableTitle>,
      dataIndex: 'departmentLead',
      key: 'departmentLead',
      align: 'right',
      render: (_, record: BillingRate) => {
        if (record.departmentLead) {
          return (
            <CheckCircleOutlined
              style={{ color: colors.criticality.positive }}
            />
          );
        }
        return <span />;
      },
      //className: 'tableAlignTop',
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.departmentLead === compB.departmentLead
          ? 0
          : compA.departmentLead > compB.departmentLead
            ? 1
            : -1,
    },
    {
      title: <TableTitle>Available Hours / Week</TableTitle>,
      dataIndex: 'availableHours',
      key: 'availableHours',
      render: (_, record: BillingRate) => (
        <span style={{ textAlign: 'right', display: 'block' }}>
          <Hours amount={record.availableHours} />
        </span>
      ),
      //className: 'tableAlignTop',
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.availableHours === compB.availableHours
          ? 0
          : compA.availableHours > compB.availableHours
            ? 1
            : -1,
    },
    {
      title: <TableTitle>Billing Rate</TableTitle>,
      dataIndex: 'billingRate',
      key: 'billingRate',
      render: (_, record: BillingRate) => (
        <span style={{ textAlign: 'right', display: 'block' }}>
          <Currency amount={record.billingRate} />
        </span>
      ),
      //className: 'tableAlignTop',
      sortDirections: ['ascend', 'descend'],
      sorter: (compA, compB) =>
        compA.billingRate === compB.billingRate
          ? 0
          : compA.billingRate > compB.billingRate
            ? 1
            : -1,
    },
    {
      title: <TableTitle>Actions</TableTitle>,
      dataIndex: '',
      width: '84px',
      render: (_, billingRateEntry) => (
        <div className="center-table-cell">
          <Tooltip title="Edit" placement="top">
            <Button
              shape="circle"
              icon={<EditOutlined />}
              style={styles.button}
              onClick={() => handleEditClicked(billingRateEntry)}
            />
          </Tooltip>
          <Tooltip title="Delete" placement="top">
            <Button
              shape="circle"
              icon={<IBDIcon name={'Delete'} />}
              style={styles.button}
              onClick={() => onAction('delete', billingRateEntry)}
            />
          </Tooltip>
        </div>
      ),
    },
  ];

  const getData = () => {
    if (
      typeof reportData !== 'undefined' &&
      reportData !== null &&
      reportData.billingRates !== null
    ) {
      return reportData.billingRates;
    }
    return [];
  };

  const handleCloseDialog = (refresh: boolean) => {
    setShowEditDialog(false);

    setAction('');

    setSelectedForEdit(null);
    if (refresh) {
      onAction('refresh', null);
    }
  };

  const handleTableChange = (
    updatedPagination: TablePaginationConfig,
    filters: SorterResult<BillingRate>,
    sorter,
  ) => {
    const { ...params } = filters;

    let sortDir: string = sorter.order === 'descend' ? '-1' : '1';
    if (typeof sorter.order === 'undefined' || sorter.order === null) {
      if (
        typeof input.sortDirection !== 'undefined' &&
        input.sortDirection !== null
      ) {
        sortDir = input.sortDirection;
      }
    }

    let sortField = input.sortField ? input.sortField : 'lastOccurrence';
    if (
      typeof sorter.field !== 'undefined' &&
      typeof sorter.order !== 'undefined'
    ) {
      sortField = sorter.field;
    }

    // @ts-ignore
    setInput({
      ...input,
      ...params,
      pageSize: updatedPagination.pageSize,
      page: updatedPagination.current,
      sortField,
      sortDirection: sortDir,
    });
  };

  return (
    <>
      <IBTable
        showSorterTooltip
        columns={columns}
        dataSource={getData()}
        onChange={handleTableChange}
        loading={loading}
        rowKey={(record) => record.id}
        pagination={{
          current: input.page,
          pageSize: input.pageSize,
          total: totalRecords,
          showSizeChanger: true,
        }}
      />
      {showEditDialog && isNotNull(selectedForEdit) && action === 'edit' && (
        <AddEditDialog
          open={showEditDialog}
          loading={loading}
          billingRateData={{
            id: selectedForEdit.id,
            name: selectedForEdit.name,
            type: selectedForEdit.type,
            email: selectedForEdit.email,
            department: selectedForEdit.department,
            departmentLead: selectedForEdit.departmentLead,
            billingRate: selectedForEdit.billingRate,
            hourlyCost: selectedForEdit.hourlyCost,
            availableHours: selectedForEdit.availableHours,
          }}
          onClose={handleCloseDialog}
        />
      )}
    </>
  );
};

export default ReportTable;
